import {
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ViewEncapsulation,
  computed,
  effect,
  input,
  output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule } from '@ionic/angular';
import { DateTime as LuxonDateTime } from 'luxon';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { TranslationPipe } from 'src/tidy-ui-components/pipes/translation.pipe';

import { Inquiry } from 'src/models/inquiry.model';

// Human labels for the platform decline-reason enum (see inquiry.model.ts).
const DECLINE_REASON_LABELS: Record<string, string> = {
  ISSUE_WITH_TRIP__DATES_NOT_AVAILABLE: 'Dates not available',
  ISSUE_WITH_TRIP__TRIP_LENGTH: "Trip length doesn't work",
  ISSUE_WITH_TRIP__INCORRECT_PRICE: "Price isn't right",
  HOUSE_RULES__PETS: 'House rules: pets',
  HOUSE_RULES__GUEST_AMOUNT: 'House rules: number of guests',
  REQUEST_SUSPICIOUS_OR_OFFENSIVE: 'Suspicious or offensive request',
  PARTY__MESSAGE_FROM_GUEST: 'Party concern: guest message',
  PARTY__GUEST_PAST_REVIEWS: 'Party concern: past reviews',
  PARTY__SOCIAL_MEDIA: 'Party concern: social media',
  PARTY__LOCAL_OR_LAST_MINUTE_OR_SINGLE_NIGHT: 'Party concern: local / last-minute / one night',
  OTHER: 'Other',
};

// Airbnb requires a non-guest-facing note (min 30 chars) for these reasons.
const AIRBNB_NOTE_REQUIRED_REASONS = new Set<string>([
  'REQUEST_SUSPICIOUS_OR_OFFENSIVE',
  'PARTY__MESSAGE_FROM_GUEST',
  'PARTY__GUEST_PAST_REVIEWS',
  'PARTY__SOCIAL_MEDIA',
  'PARTY__LOCAL_OR_LAST_MINUTE_OR_SINGLE_NIGHT',
  'OTHER',
]);

const AIRBNB_NOTE_MIN_LENGTH = 30;

// What the host page POSTs to /inquiries/:id/decline (see InquiriesService).
// Tidy declines validate nothing: `declineReason` is optional free text passed
// into the guest's decline email, so it may be absent entirely.
export interface InquiryDeclineSubmit {
  declineReason?: string;
  messageToGuest?: string;
  messageToPlatform?: string;
}

interface DeclineReasonVM {
  value: string;
  label: string;
  notesAirbnb: boolean;
}

// Everything the modal shows about the inquiry being declined.
interface DeclineContext {
  initials: string;
  guestName: string;
  propertyLine: string;
  dateRange: string;
  sourceName: string;
  subtitle: string;
  // Tidy (direct booking) skips the Airbnb reason enum + note rules entirely:
  // just one optional free-text reason shared with the guest.
  isTidy: boolean;
  reasons: DeclineReasonVM[];
}

/**
 * Decline-inquiry modal shared by the Inquiries page and the schedule's
 * inquiry detail modal: reason cards + contextual note field with a live
 * progress meter for Airbnb's 30-char rule. Pass the `Inquiry` to open
 * (null = closed); the host page owns the actual decline request.
 */
@Component({
  selector: 'app-inquiry-decline-modal',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  // The ion-modal renders in an overlay, so the styles must be global.
  encapsulation: ViewEncapsulation.None,
  templateUrl: './inquiry-decline-modal.component.html',
  styleUrls: ['./inquiry-decline-modal.component.scss'],
  imports: [CommonModule, IonicModule, TidyUiComponentsModule, TranslationPipe],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class InquiryDeclineModalComponent {
  readonly inquiry = input<Inquiry | null>(null);
  readonly busy = input<boolean>(false);

  readonly dismissed = output<void>();
  readonly submitted = output<InquiryDeclineSubmit>();

  readonly reason = signal<DeclineReasonVM | null>(null);
  readonly note = signal<string>('');
  readonly noteMinLength = AIRBNB_NOTE_MIN_LENGTH;

  readonly noteCount = computed(() => this.note().trim().length);
  readonly noteMet = computed(() => this.noteCount() >= AIRBNB_NOTE_MIN_LENGTH);
  readonly noteProgress = computed(() =>
    Math.min(100, Math.round((this.noteCount() / AIRBNB_NOTE_MIN_LENGTH) * 100))
  );
  readonly noteHint = computed(() => {
    const remaining = AIRBNB_NOTE_MIN_LENGTH - this.noteCount();
    if (remaining <= 0) {
      return 'Minimum length met';
    }
    return `${remaining} more character${remaining === 1 ? '' : 's'} needed`;
  });
  readonly canSubmit = computed(() => {
    if (this.busy()) {
      return false;
    }
    // Tidy validates nothing — the reason is optional free text.
    if (this.context()?.isTidy) {
      return true;
    }
    const reason = this.reason();
    if (!reason) {
      return false;
    }
    // Every reason needs a message — Airbnb rejects a decline without one.
    // Note-to-Airbnb reasons must hit the 30-char minimum; the rest just need
    // a non-empty guest message.
    return reason.notesAirbnb ? this.noteMet() : this.noteCount() > 0;
  });

  readonly context = computed<DeclineContext | null>(() => {
    const inquiry = this.inquiry();
    if (!inquiry) {
      return null;
    }
    const isTidy = inquiry.platform === 'tidy';
    const reasons = inquiry.decline_reasons.length
      ? inquiry.decline_reasons
      : Object.keys(DECLINE_REASON_LABELS);
    return {
      initials: this.initials(inquiry.guest.name),
      guestName: inquiry.guest.name,
      propertyLine: inquiry.property_location
        ? `${inquiry.property_name} · ${inquiry.property_location}`
        : inquiry.property_name,
      dateRange: this.dateRangeLabel(inquiry.check_in_date, inquiry.check_out_date),
      sourceName: inquiry.source_name,
      subtitle: isTidy
        ? 'Optionally tell the guest why — it goes into their decline email.'
        : `Why are you declining? ${inquiry.source_name} asks for a reason.`,
      isTidy,
      reasons: isTidy
        ? []
        : reasons.map((reason) => ({
            value: reason,
            label: DECLINE_REASON_LABELS[reason] ?? reason,
            notesAirbnb: AIRBNB_NOTE_REQUIRED_REASONS.has(reason),
          })),
    };
  });

  constructor() {
    // A new inquiry means a fresh form — drop the previous reason and note.
    effect(() => {
      this.inquiry();
      this.reason.set(null);
      this.note.set('');
    });
  }

  selectReason(reason: DeclineReasonVM): void {
    const previous = this.reason();
    // The note means something different per reason type (guest-facing chat
    // message vs private note to Airbnb) — don't carry text across that switch.
    if (previous && previous.notesAirbnb !== reason.notesAirbnb) {
      this.note.set('');
    }
    this.reason.set(reason);
  }

  onNoteInput(event: Event): void {
    this.note.set((event.target as HTMLTextAreaElement).value ?? '');
  }

  close(): void {
    if (this.busy()) {
      return;
    }
    this.dismissed.emit();
  }

  submit(): void {
    if (!this.canSubmit()) {
      return;
    }
    const note = this.note().trim();
    // Tidy: no reason enum, no note rules — just the optional guest-facing text.
    if (this.context()?.isTidy) {
      this.submitted.emit({ declineReason: note || undefined });
      return;
    }
    const reason = this.reason();
    if (!reason) {
      return;
    }
    this.submitted.emit({
      declineReason: reason.value,
      messageToGuest: reason.notesAirbnb ? undefined : note,
      messageToPlatform: reason.notesAirbnb ? note : undefined,
    });
  }

  // Luxon renders an unparseable input as the literal text "Invalid DateTime";
  // a dateless inquiry is a normal state, so guard rather than format blindly.
  private fmtDay(ymd: string | null): string | null {
    if (!ymd) {
      return null;
    }
    const day = LuxonDateTime.fromISO(ymd);
    return day.isValid ? day.toFormat('MMM d') : null;
  }

  private dateRangeLabel(checkIn: string | null, checkOut: string | null): string {
    const from = this.fmtDay(checkIn);
    const to = this.fmtDay(checkOut);
    return from && to ? `${from} – ${to}` : 'Dates not shared';
  }

  private initials(name: string): string {
    const parts = name.trim().split(/\s+/);
    const first = parts[0]?.[0] ?? '';
    const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
    return (first + last).toUpperCase();
  }
}
